<template>
  <div class="design-width content post" v-if="post">
    <router-link class="post__backlink" to="/impact">Zurück</router-link>
    <div class="post__hero">
      <figure v-if="post.previewImage">
        <img :src="post.previewImage.url" alt="" />
      </figure>
      <div class="post__hero__content">
        <h1>{{ post.title }}</h1>
        <div class="post__hero__meta">
          <span class="date">{{ post.formattedDate }}</span>
          <ul class="post__hero__tags">
            <li v-for="cat in post.categories" :key="cat.id">
              {{ cat.title }}
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="post__content">
      <template v-for="block in post.content" :key="block.id">
        <div
          v-if="block.type === 'richtextBlock'"
          class="post__text"
          v-html="block.text"
        />
      </template>
    </div>

    <a :href="ctaLink" class="post__cta" target="_blank">
      <span v-if="!post.ctaText">Let's Talk About It!
Get in touch</span>
      <span v-else>{{ post.ctaText }}</span>
    </a>

    <!-- Related Posts Section -->
    <div class="post__related" v-if="randomPosts.length > 0">
      <h2 class="post__related__headline">Weitere Artikel</h2>
      <div class="post__related__grid">
        <BlogItem
          v-for="relatedPost in randomPosts"
          :key="relatedPost.slug"
          :post="relatedPost"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import BlogItem from '../components/blog-item.vue';
import { ax, baseURL } from '../js/api';
import { pageMeta, pageTitle } from '../js/meta';

export default defineComponent({
  components: {
    BlogItem,
  },
  data: function () {
    return {
      postSlug: null,
      post: null,
      allPosts: [],
    };
  },
  computed: {
    randomPosts() {
      if (!this.allPosts.data || !this.post) {
        return [];
      }

      // Filter out the current post
      const otherPosts = this.allPosts.data.filter(
        (p) => p.slug !== this.post.slug
      );

      // Shuffle and take first 3
      const shuffled = [...otherPosts].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 3);
    },
    ctaLink() {
      if (this.post.ctaLink) {
        return this.post.ctaLink;
      }
      return `mailto:info@hochspannung.ch`;
    },
  },
  metaInfo() {
    const post = this.post || {};
    return pageMeta({
      title: pageTitle(post.title),
      description: post.previewText,
      image: post.previewImage && post.previewImage.url,
      type: 'article',
    });
  },
  created() {
    this.getPost();

    // Fetch all blog posts for related posts
    ax.get(`${baseURL}/api/blog`)
      .then((response) => {
        this.allPosts = response.data;
      })
      .catch(function (error) {
        //console.error(error);
      });
  },
  watch: {
    // The component is reused when navigating between posts.
    '$route.params.post'(slug) {
      if (slug) this.getPost();
    },
  },
  methods: {
    getPost() {
      const slug = this.$route.params.post;
      this.postSlug = slug;
      ax.get(`${baseURL}/api/blog/${slug}`)
        .then((response) => {
          if (slug === this.$route.params.post) this.post = response.data;
        })
        .catch(function (error) {
          //console.error(error);
        });
    },
  },
});
</script>

<style lang="scss">
@import '../styles/_toolbox';

.post {
  font-size: 2.5rem;
  max-width: 80ch;
  margin: 0 auto;
  position: relative;

  &__backlink {
    position: absolute;
    top: 0;
    font-size: 0.5em;
    text-decoration: underline;
    left: 0.5em;
    text-transform: uppercase;

    @include viewport(phone) {
      display: none;
    }
  }

  &__hero {
    display: flex;
    gap: 4rem;
    margin-bottom: 4rem;

    figure {
      width: 50%;
      flex-shrink: 0;

      img {
        display: block;
        width: 100%;
        height: auto;
      }
    }

    &__content {
      width: 50%;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
    }

    &__meta {
      display: flex;
      justify-content: space-between;
      margin-top: auto;
    }

    .date {
      font-weight: 300;
    }

    &__tags {
      display: flex;
      gap: 1rem;
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        border: 1px solid var(--c-brand);
        padding: 0.3em 1em;
        border-radius: 2rem;
        font-size: 0.75em;
        color: var(--c-brand);
      }
    }

    h1 {
      font-size: 2.5em;
      hyphens: auto;
      margin-bottom: 1em;
    }
  }

  &__cta {
    white-space: pre-wrap;
    margin: 6rem auto;
    display: block;
    font-size: 1.5em;
    background-color: var(--c-brand);
    color: #fff;
    font-weight: 700;
    padding: 0.5em 1em;
    line-height: 1.3;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      right: 1em;
      top: 50%;
      transform: translateY(-50%);
      width: 1.1em;
      height: 0.65em;
      background: url('data:image/svg+xml,<svg width="46" height="27" viewBox="0 0 46 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M45.5 13.5L31 27L28.959 24.8018L39.5146 15H0V12H39.5146L28.959 2.19824L31 0L45.5 13.5Z" fill="white"/></svg>');
      background-size: contain;
      transition: transform 0.3s ease;
    }

    &:hover {
      &::after {
        transform: translateY(-50%) translateX(0.2em);
      }
    }
  }

  &__text {
    line-height: 1.3;

    p {
      margin-top: 1em;
      font-weight: 400;

      &:first-child {
        margin-top: 0;
      }
    }

    h2,
    h3,
    h4 {
      margin-top: 1.5em;
      margin-bottom: -0.5em;
      line-height: 1.2;
      font-weight: 600;

      &:first-child {
        margin-top: 0;
      }
    }

    h2 {
      font-size: 1.5em;
    }

    h3 {
      font-size: 1.25em;
    }

    ul {
      margin-top: 1em;
      font-weight: 400;

      &:first-child {
        margin-top: 0;
      }

      li {
        list-style: none;
        margin-bottom: 1em;
        padding-left: 1.5em;
        position: relative;

        &:before {
          content: '';
          position: absolute;
          left: 0.5em;
          top: 0.5em;
          width: 0.25em;
          height: 0.25em;
          border-radius: 99%;
          background: #000;
        }
      }
    }

    strong {
      font-weight: 600;
    }

    ol {
      margin-top: 1em;
      counter-reset: list;
      font-weight: 400;

      &:first-child {
        margin-top: 0;
      }

      li {
        list-style: none;
        margin-bottom: 1em;
        padding-left: 1.75em;
        position: relative;

        &:before {
          content: counter(list) '.';
          counter-increment: list;
          position: absolute;
          top: 0;
          left: 0.5em;
          font-weight: 600;
        }
      }
    }

    .table {
      margin-top: 2em;

      &:first-child {
        margin-top: 0;
      }
    }

    table {
      width: 100%;
      font-size: 0.8em;
      background-color: #ea98b2;
      font-weight: 400;

      th {
        background-color: var(--c-brand);
        color: #fff;
      }

      th,
      td {
        padding: 0.5em;
        text-align: left;
        border: 1px solid #fff;
      }
    }
  }

  &__related {
    margin-top: 6rem;
    padding-top: 4rem;

    &__headline {
      font-size: 1.5em;
      margin-bottom: 3rem;
      font-weight: 600;
      color: var(--c-brand);
    }

    &__grid {
      display: grid;
      grid-gap: 1px;
      grid-template-columns: repeat(12, 1fr);
      font-size: 1rem;

      @include viewport(from-tablet) {
        .blog__item {
          grid-column: span 4;
        }
      }
    }
  }

  @include viewport(phone) {
    font-size: 2.5rem;

    &__hero {
      flex-direction: column;
      gap: 2rem;

      figure {
        width: 100%;
      }

      &__content {
        width: 100%;
      }

      h1 {
        font-size: 2em;
      }
    }

    &__text {
      .table {
        overflow-x: auto;
      }
    }

    &__related {
      &__grid {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
